import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  CashBoxAccountingAccountDto,
  CashBoxApiDownload,
  CashBoxDto,
  CashBoxFilterSelects,
  CashBoxFormSelects,
  CashBoxGridDto,
  CashBoxListQuery,
  CashBoxListResult,
  CashBoxRequest,
  CashBoxSelectDto,
  CashBoxToneDto,
} from './cash-boxes-api.types';
import { ErpApiError, mapErpError } from './erp-api.errors';
import { ERP_API_URL } from './erp-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const CASH_BOXES_PATH = '/treasury/cash-boxes';

@Injectable({ providedIn: 'root' })
export class CashBoxesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ERP_API_URL).replace(/\/+$/, '')}${CASH_BOXES_PATH}`;

  list(query: CashBoxListQuery = {}): Observable<CashBoxListResult> {
    return this.http.get<unknown>(this.baseUrl, { params: listParams(query) }).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(unwrapList),
      catchError(error => throwError(() => mapErpError(error))),
    );
  }

  formSelects(): Promise<CashBoxFormSelects> {
    return this.safeGet(`${this.baseUrl}/form-selects`, unwrapFormSelects);
  }

  filterSelects(): Promise<CashBoxFilterSelects> {
    return this.safeGet(`${this.baseUrl}/filter-selects`, unwrapFilterSelects);
  }

  accountingAccounts(companyUuid: string): Promise<CashBoxAccountingAccountDto[]> {
    return this.safeGet(
      `${this.baseUrl}/form-selects/accounting-accounts`,
      unwrapAccountingAccounts,
      new HttpParams().set('companyUuid', companyUuid),
    );
  }

  getById(uuid: string): Promise<CashBoxDto> {
    return this.safeGet(`${this.baseUrl}/${uuid}`, unwrapCashBox);
  }

  create(payload: CashBoxRequest): Promise<CashBoxDto> {
    return this.mutate(this.http.post<unknown>(this.baseUrl, payload), unwrapCashBox);
  }

  update(uuid: string, payload: CashBoxRequest): Promise<CashBoxDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapCashBox,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(this.http.delete<unknown>(`${this.baseUrl}/${uuid}`), () => undefined);
  }

  exportExcel(query: CashBoxListQuery = {}): Promise<CashBoxApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      listParams(query, false),
      'cajas-efectivo.xlsx',
    );
  }

  private safeGet<T>(url: string, project: (body: unknown) => T, params?: HttpParams): Promise<T> {
    return this.request(
      this.http
        .get<unknown>(url, { params })
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private mutate<T>(source: Observable<unknown>, project: (body: unknown) => T): Promise<T> {
    return this.request(source, project);
  }

  private async request<T>(source: Observable<unknown>, project: (body: unknown) => T): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapErpError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<CashBoxApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, { params, observe: 'response', responseType: 'blob' })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapErpError(error);
    }
  }
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      return timer(seconds * 1000);
    }
    if (error.status >= 500 || error.status === 0) return timer(300);
  }
  return throwError(() => error);
}

function listParams(query: CashBoxListQuery, includePagination = true): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.name) params = params.set('filters[name]', query.name);
  params = setCsv(params, 'select[company]', query.companyUuids);
  params = setCsv(params, 'select[currency]', query.currencyUuids);
  params = setCsv(params, 'select[responsible]', query.responsibleUuids);
  params = setCsv(params, 'select[accountingAccount]', query.accountingAccountUuids);
  params = setCsv(params, 'select[status]', query.statusUuids);
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function setCsv(params: HttpParams, key: string, values: readonly string[] | undefined): HttpParams {
  return values?.length ? params.set(key, values.join(',')) : params;
}

function toDownload(response: HttpResponse<Blob>, fallbackFilename: string): CashBoxApiDownload {
  if (!response.body) throw new ErpApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ?? fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): CashBoxListResult {
  const page = asRecord(unwrapData(body));
  const nested = page?.['data'];
  if (!Array.isArray(nested)) throw new ErpApiError('unexpected', 0);
  const items = nested.map(item => {
    const row = normalizeGrid(item);
    if (!row) throw new ErpApiError('unexpected', 0);
    return row;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return { items, total: typeof totalRecords === 'number' ? totalRecords : items.length };
}

function unwrapCashBox(body: unknown): CashBoxDto {
  const cashBox = normalizeDetail(unwrapData(body));
  if (!cashBox) throw new ErpApiError('unexpected', 0);
  return cashBox;
}

function unwrapFormSelects(body: unknown): CashBoxFormSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    currencies: selectList(data['currencies']),
    companies: selectList(data['companies']),
    responsibleEmployees: selectList(data['responsibleEmployees']),
  };
}

function unwrapFilterSelects(body: unknown): CashBoxFilterSelects {
  const data = asRecord(unwrapData(body));
  if (!data) throw new ErpApiError('unexpected', 0);
  return {
    companies: selectList(data['companies']),
    currencies: selectList(data['currencies']),
    responsibleEmployees: selectList(data['responsibleEmployees']),
    accountingAccounts: selectList(data['accountingAccounts']),
    status: selectList(data['status']),
  };
}

function unwrapAccountingAccounts(body: unknown): CashBoxAccountingAccountDto[] {
  const data = unwrapData(body);
  if (!Array.isArray(data)) throw new ErpApiError('unexpected', 0);
  return data.map(item => {
    const account = accountingAccount(item);
    if (!account) throw new ErpApiError('unexpected', 0);
    return account;
  });
}

function normalizeGrid(value: unknown): CashBoxGridDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const company = stringField(record, 'company');
  const name = stringField(record, 'name');
  const currency = stringField(record, 'currency');
  const responsible = blankableString(record, 'responsible');
  const accountingAccountName = stringField(record, 'accountingAccount');
  const status = toneDto(record['status']);
  if (
    !uuid ||
    !company ||
    !name ||
    !currency ||
    responsible === undefined ||
    !accountingAccountName ||
    !status
  ) {
    return null;
  }
  return {
    uuid,
    company,
    name,
    currency,
    responsible,
    accountingAccount: accountingAccountName,
    status,
  };
}

function normalizeDetail(value: unknown): CashBoxDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  const currency = selectDto(record['currency']);
  const company = selectDto(record['company']);
  const responsible = optionalSelect(record, 'responsible');
  const account = accountingAccount(record['accountingAccount']);
  const status = toneDto(record['status']);
  const createdAt = stringField(record, 'createdAt');
  const updatedAt = stringField(record, 'updatedAt');
  const notes = nullableString(record, 'notes');
  if (
    !uuid ||
    !name ||
    !currency ||
    !company ||
    responsible === undefined ||
    !account ||
    !status ||
    !createdAt ||
    !updatedAt ||
    notes === undefined
  ) {
    return null;
  }
  return {
    uuid,
    name,
    currency,
    company,
    responsible,
    accountingAccount: account,
    notes,
    status,
    createdAt,
    updatedAt,
  };
}

function selectList(value: unknown): CashBoxSelectDto[] {
  if (!Array.isArray(value)) throw new ErpApiError('unexpected', 0);
  return value.map(item => {
    const option = selectDto(item);
    if (!option) throw new ErpApiError('unexpected', 0);
    return option;
  });
}

function accountingAccount(value: unknown): CashBoxAccountingAccountDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  const accountNumber = stringField(record, 'accountNumber');
  const formattedAccountNumber = stringField(record, 'formattedAccountNumber');
  const source = selectDto(record['source']);
  const type = selectDto(record['type']);
  if (
    !uuid ||
    !name ||
    !accountNumber ||
    !formattedAccountNumber ||
    !source ||
    !type ||
    typeof record['affectable'] !== 'boolean' ||
    typeof record['selectable'] !== 'boolean'
  ) {
    return null;
  }
  return {
    uuid,
    name,
    accountNumber,
    formattedAccountNumber,
    source,
    type,
    affectable: record['affectable'],
    selectable: record['selectable'],
  };
}

function nullableString(
  record: Record<string, unknown>,
  key: string,
): string | null | undefined {
  if (!(key in record) || record[key] == null) return null;
  return typeof record[key] === 'string' ? record[key] : undefined;
}

function blankableString(
  record: Record<string, unknown>,
  key: string,
): string | null | undefined {
  if (!(key in record) || record[key] == null) return null;
  const value = record[key];
  if (typeof value !== 'string') return undefined;
  return value.trim() ? value : null;
}

function optionalSelect(
  record: Record<string, unknown>,
  key: string,
): CashBoxSelectDto | null | undefined {
  if (!(key in record) || record[key] == null) return null;
  return selectDto(record[key]) ?? undefined;
}

function selectDto(value: unknown): CashBoxSelectDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = stringField(record, 'uuid');
  const name = stringField(record, 'name');
  return uuid && name ? { uuid, name } : null;
}

function toneDto(value: unknown): CashBoxToneDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const name = stringField(record, 'name');
  const toneValue = stringField(record, 'value');
  const tone = stringField(record, 'tone');
  return name && toneValue && tone ? { name, value: toneValue, tone } : null;
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}
